import { useEffect, useState, useRef } from 'react';
import { fetchBatchImagesByBookId, isStagingUploadEnabled } from '../../../../services/mediaService';
import { AlertCircle, Loader2, RotateCw, Search } from 'lucide-react';
import Button from '../../../ui/Button';
import WizardShell from '../../../ui/WizardShell';
import BottomBar from '../../../ui/BottomBar';
import BookSidePanel from '../../../ui/BookSidePanel';
import Skeleton, { SkeletonGroup } from '../../../ui/Skeleton';
import PageViewer from '../../../builder/PageViewer';
import ForewordPage from '../../../builder/ForewordPage';
import DedicationUpClose from '../../../builder/DedicationUpClose';
import PageDrawFailed from '../../../builder/PageDrawFailed';
import { CoverDrawFailedBox } from '../../../builder/CoverStatusStrip';
import { useCoverRetry } from '../../../builder/useCoverRetry';
import { MAX_DRAW_RETRIES, drawRetriesUsedUp, retryDrawing } from '../../../../services/drawRetryService';

import { forewordPrints, forewordReachesBottom } from '../../../../util/forewordPage';
import { useBuilderBack } from '../../../builder/BuilderNavContext';
import useBookPagesFromServer from '../../../../hooks/useBookPagesFromServer';
import { saveStep } from '../../../../services/stepService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { processSketchJob } from './processSketchJob';
import { resolveBookTag } from '../../../../util/bookTag';
import StatusLabel from '../../../ui/StatusLabel';
import { PDF_DOWNLOAD_PRICE } from '../../../../config/pricing';
import { FUNNEL_EVENTS, pushFunnelEvent } from '../../../../util/funnelEvents';
import { BUILDER_ACTIONS, BUILDER_STEPS, countBuilderAction, useBuilderStepVisit } from '../../../../util/builderStepTiming';
import { multipleMediaAdd } from '../../../../services/mediaService';
import useMutation from '../../../../hooks/useMutation';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { markStepAsVisited } from '../../../../redux/slice/profileSlice';
import Image from '../../../elements/Image';
import { setLineartProgressByBook, setUploadingImages, clearUploadingImages } from '../../../../redux/slice/stepSlice';
import { isEtsyUser, readEtsyReceiptId, submitEtsyOrder, finishEtsySession } from '../../../../services/etsyService';
import { giftIdOfBook, giftPagePath, markGiftBookDone } from '../../../../services/giftService';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../../../config/apiConfig';
import { setTitle } from '../../../../helpers/MetaTag';
import { initialLineartProgress } from '../../../../redux/slice/stepSlice';
import {
  addToShopifyCart,
  getCartLineMongoIdFromAddResponse,
  recordCartLineId,
  cartLineShouldRemainForBook,
} from '../../../../services/shopifyCartService';
import { loadPendingImages, loadPendingCoverImage, hasImagesSentToRunPending, clearPendingImages, clearExpectedInteriorPageCount, resolveExpectedInteriorPageCount, isLineartJobActiveForBook } from '../../../../util/pendingImageStorage';
import { cropDataForImage } from '../../../../util/autoCropRect';
import { BASE_BOOK_PRICE } from '../../../../config/pricing';
import {
  padInteriorPagesToExpectedCount,
  isInteriorGenerationComplete,
  countFinishedInteriorFromBatchRows,
  isLineartJobCompleteByCounts,
  shouldPadInteriorGridForLineart,
  lineartStatusFromRows,
  LINEART_STAGE_STARTING,
} from '../../../../util/previewInteriorGridUtils';
import CoverPreview from '../../../builder/CoverPreview';

/** "pages 3, 8 and 12" */
const pagesList = (numbers) =>
  numbers.length === 1 ? `page ${numbers[0]}` : `pages ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;

/** Extract the S3 object key from our signed image-API URL (?id=…). */
function parseImageServiceId(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const q = url.includes('?') ? url.split('?')[1] : '';
    const id = new URLSearchParams(q).get('id');
    return id ? decodeURIComponent(id) : null;
  } catch {
    return null;
  }
}

/**
 * API poll/socket refetches re-sign every URL. When the underlying image is
 * unchanged, keep the previous URL so the browser can reuse cache.
 */
function stabilizeImageUrl(prevUrl, nextUrl) {
  if (!nextUrl) return nextUrl;
  if (!prevUrl) return nextUrl;
  const prevId = parseImageServiceId(prevUrl);
  const nextId = parseImageServiceId(nextUrl);
  if (prevId && nextId && prevId === nextId) return prevUrl;
  return nextUrl;
}

function stabilizePreviewImageRow(prevRow, nextRow) {
  if (!nextRow) return nextRow;
  if (!prevRow || prevRow.id !== nextRow.id) return nextRow;
  const sketchIdChanged =
    parseImageServiceId(prevRow.sketchUrl) !== parseImageServiceId(nextRow.sketchUrl);
  return {
    ...nextRow,
    originalUrl: stabilizeImageUrl(prevRow.originalUrl, nextRow.originalUrl),
    sketchUrl: stabilizeImageUrl(prevRow.sketchUrl, nextRow.sketchUrl),
    sketchPreviewUrl: stabilizeImageUrl(prevRow.sketchPreviewUrl, nextRow.sketchPreviewUrl),
    // DB refetch does not include client-side crops; keep them unless line art changed.
    croppedSketchUrl: sketchIdChanged
      ? nextRow.croppedSketchUrl ?? null
      : nextRow.croppedSketchUrl ?? prevRow.croppedSketchUrl ?? null,
    cropData: sketchIdChanged
      ? nextRow.cropData ?? null
      : nextRow.cropData ?? prevRow.cropData ?? null,
  };
}

function previewImageRowChanged(prevRow, nextRow) {
  if (!prevRow || prevRow.id !== nextRow.id) return true;
  if (prevRow.isLoading !== nextRow.isLoading) return true;
  if (prevRow.isCover !== nextRow.isCover) return true;
  if ((prevRow.ordering ?? prevRow.index) !== (nextRow.ordering ?? nextRow.index)) return true;
  if (parseImageServiceId(prevRow.sketchUrl) !== parseImageServiceId(nextRow.sketchUrl)) return true;
  if (parseImageServiceId(prevRow.originalUrl) !== parseImageServiceId(nextRow.originalUrl)) return true;
  return false;
}

/** True when batch media shows no active line-art generation (for stopping fallback poll). */
function isPreviewBatchLineArtComplete(batchData, bookId, activeBook) {
  if (!Array.isArray(batchData)) return false;

  if (hasImagesSentToRunPending(bookId)) return false;

  const dbLineartProcessing =
    activeBook?.isProcessing === true &&
    activeBook?.processingWorkflowType === 'lineart' &&
    (activeBook?.processingPercentage ?? 0) < 100;
  if (dbLineartProcessing) return false;

  const rows = batchData.filter(
    (item) => !item.deletedAt && (item.isCover || item.AIUrl || item.isGenerating),
  );
  if (rows.some((r) => r.isGenerating)) return false;

  const interiors = rows.filter((r) => !r.isCover);
  if (interiors.length > 0 && interiors.some((r) => !r.AIUrl || !String(r.AIUrl).trim())) {
    return false;
  }

  return true;
}

// Redirect Modal Component
const RedirectModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
      <div className='bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl'>
        <div className='text-center mb-6'>
          <div className='w-16 h-16 bg-md-pink rounded-full flex items-center justify-center mx-auto mb-4'>
            <i className='fa-solid fa-arrow-up-right-from-square text-white text-2xl'></i>
          </div>
          <h3 className='text-2xl font-bold text-gray-900 mb-2'>
            Select a Plan
          </h3>
          <p className='text-gray-600'>
            You're about to be redirected to select a pricing plan for your
            coloring book.
          </p>
        </div>

        <div className='flex gap-3'>
          <button
            onClick={onClose}
            className='flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors'
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className='flex-1 px-6 py-3 bg-md-pink text-white rounded-lg font-semibold hover:opacity-90 transition-opacity'
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// const MAX_REFETCH_ATTEMPTS = 100;
// const REFETCH_INTERVAL = 20000;
// const SYNC_CHECK_INTERVAL = 5000; // COMMENTED OUT - periodic sync disabled

const applyCropToImage = async (imageUrl, cropData, outputFileName = null) => {
  return new Promise((resolve, reject) => {
    try {
      if (!cropData) {
        fetch(imageUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const file = new File(
              [blob],
              outputFileName || `image-${Date.now()}.jpg`,
              {
                type: blob.type || 'image/jpeg',
                lastModified: Date.now(),
              },
            );
            resolve(file);
          })
          .catch(reject);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;
        // An auto-crop rectangle carries the frame it was measured in; a
        // hand-made one is already in this image's pixels.
        let { x, y, width, height } = cropDataForImage(cropData, imgWidth, imgHeight);
        x = Math.max(0, Math.min(x, imgWidth - 1));
        y = Math.max(0, Math.min(y, imgHeight - 1));
        width = Math.max(1, Math.min(width, imgWidth - x));
        height = Math.max(1, Math.min(height, imgHeight - y));
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create cropped image blob'));
              return;
            }
            const file = new File(
              [blob],
              outputFileName || `cropped-${Date.now()}.jpg`,
              {
                type: 'image/jpeg',
                lastModified: Date.now(),
              },
            );
            resolve(file);
          },
          'image/jpeg',
          0.9,
        );
      };
      img.onerror = () => {
        reject(new Error('Failed to load image for cropping'));
      };
      img.src = imageUrl;
    } catch (error) {
      reject(error);
    }
  });
};

const ReviewBookContent = () => {
  const [selectedForRegen, setSelectedForRegen] = useState([]);
  const [images, setImages] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [retryingPageIds, setRetryingPageIds] = useState([]);
  const [searchParams] = useSearchParams();

  // Progress state - ONLY updated by socket events
  const [showLineartProgress, setShowLineartProgress] = useState(false);
  const [forceHideProgress, setForceHideProgress] = useState(false);
  /** After a terminal RunPod failure (no further server auto-retry), offer one-click re-queue. */
  const [lineartOfferManualRetry, setLineartOfferManualRetry] = useState(false);
  const [lineartManualRetryBusy, setLineartManualRetryBusy] = useState(false);

  const { activeBookId, forceBookRefresh, uploadingImages, uploadingCoverImage, lineartProgressByBook } =
    useSelector((state) => state.stepFlow);
  useBuilderStepVisit(BUILDER_STEPS.PREVIEW, activeBookId);

  const lineartProgress = lineartProgressByBook[activeBookId];

  const {
    images: sketchImages,
    coverImages,
    batchId,
  } = useSelector((state) => state.sketch);
  const myBooks = useSelector((state) => state.profile.myBooks);
  const activeBook = myBooks.find(({ _id }) => activeBookId === _id);
  // Purchase format: off the book (stamped at creation, survives the redirects
  // that strip the query string), with the URL as fallback for books created
  // before the tag was persisted. Single source of truth for both the checkout
  // branch and the button label — reading them from different places is how the
  // label ends up saying "Finalize & Submit" on a book that goes straight to Shopify.
  const tag = resolveBookTag(activeBook, searchParams.get('tag'));
  // A PDF-only book (PreviewPdf): same flow, its own wording and price. Never for gift or Etsy books.
  const isPdfOnly = tag === 'downloadPdf' && !giftIdOfBook(activeBook);
  const profileData = useSelector((state) => state.profile.data);
  // Etsy buyers paid on Etsy, so this screen must not send them to a checkout:
  // the final button becomes "send to print" instead. Decided from WHO IS
  // LOGGED IN (an Etsy buyer's email is always @etsy.invalid), never from a
  // localStorage flag — a flag written on claim can outlive that session on a
  // shared browser and would wrongly swap a Shopify customer's checkout button.
  // Read once here so the label and the action cannot drift apart.
  const isEtsy = isEtsyUser(profileData);
  // A Shopify gift book was paid for before it existed: it never goes to a
  // checkout, and printing waits for the recipient to send all their gift
  // books together from the gift page. Decided from the book itself.
  const giftId = giftIdOfBook(activeBook);
  const { session } = useSelector((state) => state.authProvider);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const syncTimeoutRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const prevUploadingImagesLengthRef = useRef(null);
  const dbSeededProgressRef = useRef(false);
  const stagingUploadEnabledRef = useRef(false);

  useEffect(() => {
    isStagingUploadEnabled()
      .then((enabled) => {
        stagingUploadEnabledRef.current = enabled;
      })
      .catch(() => {
        stagingUploadEnabledRef.current = false;
      });
  }, []);

  const uploadingImagesRef = useRef(uploadingImages);
  useEffect(() => {
    uploadingImagesRef.current = uploadingImages;
  }, [uploadingImages]);

  const lineartProgressRef = useRef(lineartProgress);
  useEffect(() => {
    lineartProgressRef.current = lineartProgress;
  }, [lineartProgress]);

  const activeBookRef = useRef(activeBook);
  useEffect(() => {
    activeBookRef.current = activeBook;
  }, [activeBook]);

  const SHOPIFY_URL = process.env.REACT_APP_SHOPIFY_URL;

  const { mutate: uploadToBackend } = useMutation({
    mutationFn: async (payload) => {
      const res = await multipleMediaAdd(payload);
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        // Success
      } else {
        toast.warning(res.message || 'Unexpected response.');
      }
    },
    onError: (err) => {
      console.error('Upload failed:', err);
      toast.error('Upload failed for regenerated image.');
    },
  });

  const onHideProgress = () => {
    setShowLineartProgress(false);
    setForceHideProgress(true);

    // Clear uploading images so the render condition fully hides the bar
    dispatch(setUploadingImages([]));

    setTimeout(() => {
      dispatch(
        setLineartProgressByBook({
          ...initialLineartProgress,
          bookId: activeBookId,
        }),
      );
    }, 500);
  };

  /** Mark line art done in Redux + local pending when every page on screen is ready. */
  const finalizeLineartGeneration = (interiorCount) => {
    if (!activeBookId) return;
    const batchTotal = resolveExpectedInteriorPageCount(activeBookId, {
      activeBook,
      lineartProgress,
    });
    clearPendingImages(activeBookId);
    clearExpectedInteriorPageCount(activeBookId);
    dispatch(clearUploadingImages());
    const finished = Math.max(0, Number(interiorCount) || 0);
    const totalForProgress = batchTotal > 0 ? batchTotal : finished;
    dispatch(
      setLineartProgressByBook({
        bookId: activeBookId,
        isProcessing: false,
        overallPercentage: 100,
        currentStage: 'Completed',
        ...(totalForProgress > 0
          ? { completedImages: finished, totalImages: totalForProgress }
          : {}),
      }),
    );
    onHideProgress();
  };

  useEffect(() => {
    // Show progress from Redux OR from DB fields on the active book
    // Only show for lineart processing (not cover-only)
    const hasInFlightPending = hasImagesSentToRunPending(activeBookId);
    
    const dbIsProcessing = activeBook?.isProcessing === true
      && (activeBook?.processingPercentage ?? 0) < 100;
    // Only treat the DB job as line art when positively confirmed.
    const isLineartJob = activeBook?.processingWorkflowType === 'lineart';

    if (lineartProgress?.isProcessing || (dbIsProcessing && isLineartJob) || hasInFlightPending) {
      console.log('Lineart processing active (Redux, DB, or Pending images found)');
      setShowLineartProgress(true);
      setForceHideProgress(false);

      // Seed Redux progress with DB percentage so the bar isn't empty
      if (dbIsProcessing && !lineartProgress?.isProcessing) {
        dbSeededProgressRef.current = true;
        dispatch(setLineartProgressByBook({
          bookId: activeBookId,
          isProcessing: true,
          overallPercentage: activeBook.processingPercentage || 0,
          ...(activeBook.processingExpectedInteriorCount
            ? { totalImages: activeBook.processingExpectedInteriorCount }
            : {}),
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (syncTimeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(syncTimeoutRef.current);
      }
      if (checkIntervalRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  // The book's pages from the server, refreshed every 5 s while drawing (and
  // the same source Page photos reads, so both steps show the same progress).
  const dbLineartProcessing =
    activeBook?.isProcessing === true &&
    activeBook?.processingWorkflowType === 'lineart' &&
    (activeBook?.processingPercentage ?? 0) < 100;
  const {
    rows: batchImagesData,
    isLoading: isLoadingBatchImages,
    isFresh: batchImagesFresh,
    refetch: refetchBatchImages,
  } = useBookPagesFromServer(activeBookId, {
    poll:
      !forceHideProgress &&
      (showLineartProgress || !!lineartProgress?.isProcessing || dbLineartProcessing),
    isDone: (rows) =>
      lineartProgress?.currentStage !== LINEART_STAGE_STARTING &&
      isPreviewBatchLineArtComplete(rows, activeBookId, activeBook),
    refreshKey: forceBookRefresh,
    restartKey: [
      showLineartProgress,
      lineartProgress?.isProcessing,
      lineartProgress?.currentStage,
      activeBook?.isProcessing,
      activeBook?.processingPercentage,
      activeBook?.processingWorkflowType,
    ].join('|'),
  });

  const refetchBatchImagesRef = useRef(refetchBatchImages);
  refetchBatchImagesRef.current = refetchBatchImages;

  useEffect(() => {
    if (batchImagesData) {
      // Load pending images from localStorage to show as placeholders
      const pendingImages = loadPendingImages(activeBookId);
      const pendingCover = loadPendingCoverImage(activeBookId);

      const dbImages = batchImagesData
        // Failed pages stay too: they have no drawing and aren't drawing, but Preview shows them (PreviewFailed).
        .filter((item) => (item.isCover || item.AIUrl || item.isGenerating || item.drawStatus === 'failed') && !item.deletedAt)
        .map((item) => {
          // Check if this DB image is currently being processed as a crop/pending item in localStorage.
          // A page whose drawing is in the DB is done, whatever the local flags say: they only clear
          // if this tab hears the socket event, and a phone that slept through it would spin forever.
          // (/run nulls AIUrl on re-sent pages before the flags are set, so old art can't count.)
          const isPending =
            !item.AIUrl &&
            pendingImages.some(p => (p.id === item._id || p.publicId === item.publicId) && p.sentToRun);
          // A cover that already has its drawing (AIUrl) is done, whatever the
          // local flags say: they only clear if this tab hears the socket event.
          const isPendingCover =
            item.isCover &&
            !item.AIUrl &&
            ((pendingCover &&
              (pendingCover.id === item._id || pendingCover.publicId === item.publicId) &&
              pendingCover.sentToRun) ||
              uploadingCoverImage);
          const pageOrdering = typeof item.ordering === 'number' ? item.ordering : undefined;

          return {
            id: item._id,
            originalUrl: item.url,
            sketchUrl: item.AIUrl,
            // Web-sized variant for on-screen display only; sketchUrl stays the
            // full line art for identity, dedup, crop, and regeneration.
            sketchPreviewUrl: item.AIUrlPreview || null,
            isCover: item.isCover,
            regenerationProcessing: item.isGenerating,
            regenerateCount: item.regeneratedCount,
            drawStatus: item.drawStatus || null,
            drawRetries: item.drawRetries || 0,
            publicId: item.publicId, // Store publicId for deduping
            ordering: pageOrdering,
            index: pageOrdering,
            cropData: null,
            croppedSketchUrl: null,
            fromDB: true,
            isLoading: item.isGenerating || 
                       isPending || 
                       isPendingCover || 
                       uploadingImages.includes(item._id) || 
                       uploadingImages.includes(item.publicId)
          };
        });

      // Create placeholders for pending pages that aren't in the DB yet
      // Dedup by publicId or id
      const dbPublicIds = new Set(dbImages.map(img => img.publicId).filter(Boolean));
      const dbIds = new Set(dbImages.map(img => img.id).filter(Boolean));

      const placeholders = pendingImages
        .filter(p => !dbPublicIds.has(p.publicId) && !dbIds.has(p.id))
        .map(p => ({
          id: p.id,
          originalUrl: p.url,
          sketchUrl: null, // Still processing
          isCover: false,
          isLoading: true,
          publicId: p.publicId,
          ordering: typeof p.gridOrdering === 'number' ? p.gridOrdering : undefined,
          index: typeof p.gridOrdering === 'number' ? p.gridOrdering : undefined,
          fromDB: false
        }));

      // Merge DB images and placeholders, sorted by book page order
      let transformed = [...dbImages, ...placeholders];

      // Also handle cover placeholder if not in DB
      const dbHasCover = dbImages.some(img => img.isCover);
      if (!dbHasCover && pendingCover) {
        transformed.unshift({
          id: pendingCover.id,
          originalUrl: pendingCover.url,
          sketchUrl: null,
          isCover: true,
          isLoading: true,
          publicId: pendingCover.publicId,
          fromDB: false
        });
      }

      const coverItems = transformed.filter((img) => img.isCover);
      let pageItems = transformed
        .filter((img) => !img.isCover)
        .sort((a, b) => {
          const ao = a.ordering ?? a.index ?? 999;
          const bo = b.ordering ?? b.index ?? 999;
          return ao - bo;
        });

      const expectedInteriorCount = resolveExpectedInteriorPageCount(activeBookId, {
        activeBook,
        lineartProgress,
      });
      const shouldPadInteriorGrid = shouldPadInteriorGridForLineart(
        pageItems,
        expectedInteriorCount,
        activeBookId,
        {
          activeBook,
          lineartProgress,
          showLineartProgress,
        },
      );

      if (shouldPadInteriorGrid) {
        pageItems = padInteriorPagesToExpectedCount(
          pageItems,
          expectedInteriorCount,
          pendingImages,
        );
      }

      transformed = [...coverItems, ...pageItems];

      setImages((prevImages) => {
        const prevById = new Map(prevImages.map((img) => [String(img.id), img]));
        const stabilized = transformed.map((row) => {
          const prev = prevById.get(String(row.id));
          return prev ? stabilizePreviewImageRow(prev, row) : row;
        });

        const prevPageIds = prevImages
          .filter((img) => !img.isCover)
          .map((img) => img.id)
          .join(',');
        const nextPageIds = stabilized
          .filter((img) => !img.isCover)
          .map((img) => img.id)
          .join(',');

        if (prevPageIds !== nextPageIds) return stabilized;

        const hasLengthChange = prevImages.length !== stabilized.length;
        if (hasLengthChange) return stabilized;

        const hasContentChange = stabilized.some((newImg) => {
          const prevImg = prevById.get(String(newImg.id));
          return previewImageRowChanged(prevImg, newImg);
        });

        if (hasContentChange) return stabilized;
        return prevImages;
      });
    }
  }, [
    batchImagesData,
    activeBookId,
    uploadingCoverImage,
    showLineartProgress,
    lineartProgress?.isProcessing,
    lineartProgress?.totalImages,
    activeBook?.processingExpectedInteriorCount,
    activeBook?.isProcessing,
    activeBook?.processingWorkflowType,
    activeBook?.processingPercentage,
  ]);

  useEffect(() => {
    const currentLength = uploadingImages?.length || 0;
    const prevLength = prevUploadingImagesLengthRef.current;

    prevUploadingImagesLengthRef.current = currentLength;

    if (prevLength !== null && currentLength < prevLength) {
      console.log(
        `📉 uploadingImages decreased: ${prevLength} → ${currentLength}, refetching batch images...`,
      );
      setTimeout(async () => {
        try {
          await refetchBatchImages();
          console.log('✅ Batch images refetched after upload completion');
        } catch (error) {
          console.error('❌ Failed to refetch after upload completion:', error);
        }
      }, 1000);
    }
  }, [uploadingImages?.length, refetchBatchImages]);

  setTitle('Memory Doodle | Preview Book');

  // COMMENTED OUT - periodic sync disabled
  // const needsSync = () => {
  //   const totalReduxImages =
  //     (sketchImages?.length || 0) + (coverImages?.length || 0);
  //   const currentLocalImages = images.length;
  //   console.log(
  //     `🔍 Sync Check: Local=${currentLocalImages}, Redux=${totalReduxImages}`,
  //   );
  //   return currentLocalImages !== totalReduxImages;
  // };

  // COMMENTED OUT - periodic sync disabled
  // const performSyncRefetch = async (attemptNumber) => {
  //   if (!isMountedRef.current || attemptNumber >= MAX_REFETCH_ATTEMPTS) {
  //     if (attemptNumber >= MAX_REFETCH_ATTEMPTS) {
  //       console.log('⏹️ Max sync attempts reached. Stopping auto-sync.');
  //     }
  //     setIsSyncing(false);
  //     setSyncAttempts(0);
  //     return;
  //   }
  //
  //   try {
  //     console.log(
  //       `🔄 Syncing data... (Attempt ${attemptNumber + 1}/${MAX_REFETCH_ATTEMPTS})`,
  //     );
  //     setIsSyncing(true);
  //     setSyncAttempts(attemptNumber + 1);
  //
  //     await refetchBatchImages();
  //
  //     setTimeout(() => {
  //       if (!isMountedRef.current) return;
  //
  //       if (needsSync()) {
  //         console.log(
  //           `⏳ Still not synced. Retrying in ${REFETCH_INTERVAL / 1000} seconds...`,
  //         );
  //         syncTimeoutRef.current = setTimeout(() => {
  //           performSyncRefetch(attemptNumber + 1);
  //         }, REFETCH_INTERVAL);
  //       } else {
  //         console.log('✅ Sync successful!');
  //         setIsSyncing(false);
  //         setSyncAttempts(0);
  //       }
  //     }, 1000);
  //   } catch (error) {
  //     console.error('❌ Sync refetch error:', error);
  //     if (attemptNumber < MAX_REFETCH_ATTEMPTS) {
  //       console.log(
  //         `⚠️ Sync error. Retrying in ${REFETCH_INTERVAL / 1000} seconds...`,
  //       );
  //       syncTimeoutRef.current = setTimeout(() => {
  //         performSyncRefetch(attemptNumber + 1);
  //       }, REFETCH_INTERVAL);
  //     } else {
  //       console.log('⏹️ Max sync attempts reached after errors.');
  //       setIsSyncing(false);
  //       setSyncAttempts(0);
  //     }
  //   }
  // };

  // COMMENTED OUT - periodic sync interval disabled
  // useEffect(() => {
  //   if (!images.length || isLoadingBatchImages) return;
  //
  //   checkIntervalRef.current = setInterval(() => {
  //     if (!isMountedRef.current || isSyncing) return;
  //
  //     if (needsSync()) {
  //       console.log('Sync mismatch detected. Starting auto-sync...');
  //       performSyncRefetch(0);
  //     }
  //   }, SYNC_CHECK_INTERVAL);
  //
  //   return () => {
  //     if (checkIntervalRef.current) {
  //       clearInterval(checkIntervalRef.current);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [
  //   images.length,
  //   sketchImages?.length,
  //   coverImages?.length,
  //   isLoadingBatchImages,
  //   isSyncing,
  //   rebatch,
  // ]);

  const urlToFile = async (url, filename) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, {
      type: blob.type || 'image/jpeg',
      lastModified: Date.now(),
    });
  };



  const handleRegenerate = async (ids = selectedForRegen) => {
    if (ids.length === 0) return;

    setIsRegenerating(true);

    try {
      const imagesToRegenerate = images.filter(
        (img) => ids.includes(img.id) && !img.isCover,
      );

      if (imagesToRegenerate.length === 0) {
        toast.error('No valid images selected for regeneration.');
        return;
      }
      countBuilderAction(BUILDER_ACTIONS.PAGES_REGENERATED, imagesToRegenerate.length);

      console.log(
        `🔄 Starting regeneration for ${imagesToRegenerate.length} images...`,
      );

      const processableImages = await Promise.all(
        imagesToRegenerate.map(async (img, index) => {
          try {
            const filename = `regenerate-${img.id}-${Date.now()}.jpg`;

            let file;
            if (img.cropData) {
              console.log(`✂️ Applying crop to original image for ${img.id}`);
              file = await applyCropToImage(
                img.originalUrl,
                img.cropData,
                filename,
              );
            } else {
              file = await urlToFile(img.originalUrl, filename);
            }

            return {
              id: img.id,
              isEditable: true,
              regenerateCount: img.regenerateCount,
              file: file,
              url: img.originalUrl,
              cropData: img.cropData,
            };
          } catch (error) {
            console.error(`❌ Failed to process image ${img.id}:`, error);
            toast.error(`Failed to process image for regeneration.`);
            return null;
          }
        }),
      );

      const validImages = processableImages.filter((img) => img !== null);

      if (validImages.length === 0) {
        toast.error('Failed to prepare images for regeneration.');
        return;
      }

      setSelectedForRegen([]);

      await processSketchJob({
        images: validImages,
        uploadToBackend,
        uploadToBackend,
        batchId: batchId || uuidv4(),
        onCoverReady: null,
        bookId: activeBookId,
        shouldRegenerate: true,
        onImageComplete: async (imageIndex, completedImage) => {
          console.log(
            `Image ${imageIndex + 1} completed, triggering refetch...`,
          );
          setTimeout(async () => {
            await refetchBatchImages();
            console.log(`Refetch completed for image ${imageIndex + 1}`);
          }, 2000);
        },
        onError: () => {
          setIsRegenerating(false);
        },
      });

      toast.success(`Regeneration completed for ${validImages.length} images.`);
    } catch (error) {
      console.error('❌ Regeneration error:', error);
      toast.error('Failed to start regeneration process.');
    } finally {
      setIsRegenerating(false);
    }
  };

  /** Re-queue line art for pages that never received AI output (after a failed/timed-out run). */
  const handleRetryLineartAfterFailure = async () => {
    if (!activeBookId) return;
    const interiors = images.filter((img) => !img.isCover && img.originalUrl);
    const missingLineart = interiors.filter((img) => !img.AIUrl);
    const targets = missingLineart.length > 0 ? missingLineart : interiors;
    if (targets.length === 0) {
      toast.error('No page images found to send. Try refreshing the page.');
      return;
    }
    countBuilderAction(BUILDER_ACTIONS.GENERATION_RETRIED);

    setLineartManualRetryBusy(true);
    setLineartOfferManualRetry(false);
    setShowLineartProgress(true);
    setForceHideProgress(false);
    dispatch(
      setLineartProgressByBook({
        bookId: activeBookId,
        isProcessing: true,
        overallPercentage: Math.min(lineartProgress?.overallPercentage || 5, 95),
        currentStage: 'Restarting line art…',
        totalImages: targets.length,
        currentImage: 0,
        completedImages: 0,
      }),
    );

    try {
      const processable = await Promise.all(
        targets.map(async (img) => {
          try {
            const filename = `retry-lineart-${img.id}-${Date.now()}.jpg`;
            let file;
            if (img.cropData) {
              file = await applyCropToImage(img.originalUrl, img.cropData, filename);
            } else {
              file = await urlToFile(img.originalUrl, filename);
            }
            return {
              id: img.id,
              _id: img._id,
              publicId: img.publicId,
              isEditable: true,
              file,
              url: img.originalUrl,
              cropData: img.cropData,
            };
          } catch (e) {
            console.error('retry lineart prep', img.id, e);
            return null;
          }
        }),
      );
      const valid = processable.filter(Boolean);
      if (valid.length === 0) {
        throw new Error('Could not prepare images for line art.');
      }

      await processSketchJob({
        images: valid,
        uploadToBackend,
        batchId: batchId || uuidv4(),
        onCoverReady: null,
        bookId: activeBookId,
        shouldRegenerate: true,
        onError: () => {
          setLineartOfferManualRetry(true);
          setShowLineartProgress(false);
        },
      });

      toast.info(`Sent ${valid.length} page(s) to line art again.`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Could not restart line art.');
      setLineartOfferManualRetry(true);
      setShowLineartProgress(false);
    } finally {
      setLineartManualRetryBusy(false);
    }
  };

  const getVariantId = (item) => {
    if (!item || typeof item !== 'object') return null;
    return item.shopifyVariantId || item.variantId || item.id || null;
  };

  /**
   * Etsy equivalent of checkout: the book is done, send it to print.
   *
   * Passes the book id so the API can link it to the Etsy order — the order was
   * created at claim time, before any book existed, so this is where the two
   * are joined.
   */
  /** End the Etsy buyer's session and show the confirmation (see finishEtsySession). */
  const finishEtsyBuild = finishEtsySession;

  // The header's Back runs the same handler as the bottom bar's once the page has rendered.
  const headerBackRef = useRef(null);
  useBuilderBack(() => (headerBackRef.current ? headerBackRef.current() : false));

  const handleEtsySubmit = async () => {
    if (isCheckoutLoading) return;
    setIsCheckoutLoading(true);
    try {
      const receiptId = readEtsyReceiptId();
      const result = await submitEtsyOrder(receiptId, activeBookId);

      if (!result || !result.ok) {
        // "Already sent" is the common case (a double-click, or coming back to
        // a finished book), and is not an error worth alarming anyone about.
        toast.info((result && result.message) || 'This book has already been sent to us.');
        finishEtsyBuild({ format: result && result.format, copies: result && result.copies });
        return;
      }

      pushFunnelEvent(FUNNEL_EVENTS.BOOK_APPROVED, {
        bookId: activeBookId,
        profile: profileData,
        tag,
      });
      finishEtsyBuild({ format: result.format, copies: result.copies });
    } catch (error) {
      console.error('Etsy submit failed:', error);
      toast.error('We could not send your book. Please try again.');
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleGiftDone = async () => {
    if (isCheckoutLoading) return;
    setIsCheckoutLoading(true);
    try {
      const result = await markGiftBookDone(giftId, activeBookId);
      if (!result?.success) {
        toast.error(result?.message || 'We could not save that. Please try again.');
        return;
      }
      pushFunnelEvent(FUNNEL_EVENTS.BOOK_APPROVED, { bookId: activeBookId, profile: profileData, tag });
      navigate(giftPagePath(giftId));
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleAddToCart = async (navigateToCart = false) => {
    try {
      const bookData = myBooks.find(({ _id }) => activeBookId === _id);

      if (!bookData) {
        toast.error('Book data not found. Please try again.');
        return;
      }

      pushFunnelEvent(FUNNEL_EVENTS.BOOK_APPROVED, {
        bookId: activeBookId,
        profile: profileData,
        tag,
      });

      // A PDF-only book goes into the order like a printed one (decided 2026-10-26):
      // Review order prices it as the $9.95 PDF and checks it out as the PDF product.

      // ORIGINAL LOGIC: Add to cart flow (unchanged)
      let existingCart = [];
      try {
        const raw = localStorage.getItem('selectedPlan');
        if (raw) {
          const parsed = JSON.parse(raw);
          existingCart = Array.isArray(parsed) ? parsed : [parsed];
        }
      } catch (e) {
        console.error('Error reading cart:', e);
        existingCart = [];
      }

      const beforeStaleFilter = existingCart.length;
      existingCart = existingCart.filter(
        (item) =>
          item.bookId !== activeBookId ||
          cartLineShouldRemainForBook(item, bookData),
      );
      if (existingCart.length !== beforeStaleFilter) {
        if (!existingCart.length) {
          localStorage.removeItem('selectedPlan');
        } else {
          localStorage.setItem('selectedPlan', JSON.stringify(existingCart));
        }
        window.dispatchEvent(new Event('cartUpdated'));
      }

      const bookAlreadyInCart = existingCart.some(
        (item) => item.bookId === activeBookId,
      );

      if (bookAlreadyInCart) {
        console.log('📚 Book already in cart, navigating to cart');
        navigate('/finalize-order');
        return;
      }

      // Helper to update local storage and trigger async API sync
      const updateLocalAndSync = (cartArray, itemToSync, isIncrement = false) => {
        localStorage.setItem('selectedPlan', JSON.stringify(cartArray));
        window.dispatchEvent(new Event('cartUpdated'));

        // Fire & forget async API call
        if (itemToSync) {
          const payload = {
            bookId: itemToSync.bookId,
            shopifyVariantId: itemToSync.shopifyVariantId || itemToSync.variantId || itemToSync.id,
            shopifyProductId: itemToSync.shopifyProductId || itemToSync.productId || "",
            quantity: isIncrement ? 1 : (itemToSync.quantity || 1), // If incrementing, API just needs +1 to its existing logic
            title: itemToSync.title,
            name: itemToSync.name,
            description: itemToSync.description,
            image: itemToSync.image,
            previewUrl: itemToSync.previewUrl,
            coverImage: itemToSync.coverImage,
            planPricingData: itemToSync.planPricingData,
            recipient: itemToSync.recipient,
            version: itemToSync.version,
            features: itemToSync.features,
            offerType: itemToSync.offerType,
            highLightTag: itemToSync.highLightTag,
            highLightText: itemToSync.highLightText
          };

          addToShopifyCart(payload)
            .then(res => recordCartLineId(itemToSync, getCartLineMongoIdFromAddResponse(res)))
            .catch(err => console.error("Async cart add failed:", err));
        }
      };

      let unassignedPlans = [];
      try {
        const raw = localStorage.getItem('unassignedPlans');
        if (raw) {
          const parsed = JSON.parse(raw);
          unassignedPlans = Array.isArray(parsed) ? parsed : [parsed];
        }
      } catch (e) {
        console.error('Error reading unassigned plans:', e);
      }

      if (unassignedPlans.length > 0) {
        console.log('📦 Unassigned plans found, using original flow');

        unassignedPlans.forEach((plan) => {
          const planVariantId = getVariantId(plan);

          if (!planVariantId) {
            console.warn('Skipping plan without variant ID:', plan);
            return;
          }

          const existingIndex = existingCart.findIndex(
            (item) =>
              item.bookId === activeBookId &&
              getVariantId(item) === planVariantId,
          );

          if (existingIndex >= 0) {
            existingCart[existingIndex].quantity += 1;
            existingCart[existingIndex].planPricingData.quantity += 1;
            if (!existingCart[existingIndex].createdAt) {
              existingCart[existingIndex].createdAt = new Date().toISOString();
            }
            console.log(
              `➕ Incremented quantity for existing plan (${planVariantId})`,
            );
          } else {
            const cartItem = {
              id: `book-${activeBookId}-${planVariantId}`,
              bookId: activeBookId,
              title:
                plan.title ||
                plan.name ||
                bookData.title ||
                'Custom Coloring Book',
              name: plan.name || plan.title,
              description: plan.description,
              quantity: plan.quantity || 1,
              shopifyVariantId: plan.shopifyVariantId || planVariantId,
              shopifyProductId: plan.shopifyProductId || plan.productId,
              planPricingData: {
                price: plan.planPricingData?.price || BASE_BOOK_PRICE,
                unitPrice: plan.planPricingData?.unitPrice || BASE_BOOK_PRICE,
                quantity: plan.quantity || 1,
                priceDescription:
                  plan.planPricingData?.priceDescription || 'Select Plan',
                currencyCode: plan.planPricingData?.currencyCode || 'USD',
              },
              image: plan.image || bookData.coverImage || bookData.previewUrl,
              highLightTag: plan.highLightTag || 0,
              highLightText: plan.highLightText || '',
              features: plan.features || [],
              offerType: plan.offerType,
              recipient: bookData.recipientName || '',
              version: bookData.version || 1,
              previewUrl: bookData.previewUrl || bookData.coverImage,
              coverImage: bookData.coverImage || bookData.previewUrl,
              createdAt: new Date().toISOString(),
            };

            existingCart.push(cartItem);
            console.log(`🆕 Added new plan to cart for book ${activeBookId}`);
            updateLocalAndSync(existingCart, cartItem);
          }
        });

        localStorage.removeItem('unassignedPlans');

        console.log('✅ Cart updated with unassigned plans:', existingCart);

        if (navigateToCart) {
          navigate('/finalize-order');
        }
        return;
      }

      if (existingCart.length === 0) {
        console.log('🆕 Empty cart detected, adding book with base plan');
      } else {
        const allVariantIds = existingCart
          .map(getVariantId)
          .filter((id) => id !== null);

        if (allVariantIds.length === 0) {
          console.log(
            '🆕 No valid variants in cart, adding book with base plan',
          );
        } else {
          const firstVariantId = allVariantIds[0];
          const allSameVariant = allVariantIds.every(
            (id) => id === firstVariantId,
          );

          if (allSameVariant) {
            console.log('📋 All same variant, cloning plan for new book');

            const templatePlan = existingCart[0];
            const variantId = getVariantId(templatePlan);

            const cartItem = {
              id: `book-${activeBookId}-${variantId}`,
              bookId: activeBookId,
              title:
                templatePlan.title ||
                templatePlan.name ||
                bookData.title ||
                'Custom Coloring Book',
              name: templatePlan.name || templatePlan.title,
              description: templatePlan.description,
              quantity: 1,
              shopifyVariantId: variantId,
              shopifyProductId:
                templatePlan.shopifyProductId || templatePlan.productId,
              planPricingData: {
                price: templatePlan.planPricingData?.price || BASE_BOOK_PRICE,
                unitPrice: templatePlan.planPricingData?.unitPrice || BASE_BOOK_PRICE,
                quantity: 1,
                priceDescription:
                  templatePlan.planPricingData?.priceDescription ||
                  'Select Plan',
                currencyCode:
                  templatePlan.planPricingData?.currencyCode || 'USD',
              },
              image:
                templatePlan.image ||
                bookData.coverImage ||
                bookData.previewUrl,
              highLightTag: templatePlan.highLightTag || 0,
              highLightText: templatePlan.highLightText || '',
              features: templatePlan.features || [],
              offerType: templatePlan.offerType,
              recipient: bookData.recipientName || '',
              version: bookData.version || 1,
              previewUrl: bookData.previewUrl || bookData.coverImage,
              coverImage: bookData.coverImage || bookData.previewUrl,
              createdAt: new Date().toISOString(),
            };

            existingCart.push(cartItem);
            updateLocalAndSync(existingCart, cartItem);

            console.log('✅ Cart updated with cloned plan:', existingCart);

            if (navigateToCart) {
              navigate('/finalize-order');
            }
            return;
          } else {
            console.log(
              '🆕 Mixed variants detected, adding book with base plan',
            );
          }
        }
      }

      const basePlan = {
        id: 'gid://shopify/ProductVariant/47524057547000',
        productId: 'gid://shopify/Product/9314844639480',
        variantId: 'gid://shopify/ProductVariant/47524057547000',
        name: 'Personalized Coloring Book',
        title: 'Personalized Coloring Book',
        description:
          'Turn your treasured family photos into a beautiful, 20-page custom coloring book.',
        quantity: 1,
        planPricingData: {
          price: BASE_BOOK_PRICE,
          unitPrice: BASE_BOOK_PRICE,
          quantity: 1,
          priceDescription: 'Select Plan',
          currencyCode: 'USD',
        },
        image:
          'https://cdn.shopify.com/s/files/1/0780/2827/5960/files/Product_Image_1.jpg?v=1758836019',
        highLightTag: 0,
        highLightText: 'Most Popular',
        features: [],
      };

      const cartItem = {
        ...basePlan,
        id: `book-${activeBookId}-${basePlan.variantId}`,
        bookId: activeBookId,
        shopifyVariantId: basePlan.variantId,
        shopifyProductId: basePlan.productId,
        recipient: bookData.recipientName || '',
        version: bookData.version || 1,
        previewUrl: bookData.previewUrl || bookData.coverImage,
        coverImage: bookData.coverImage || bookData.previewUrl,
        createdAt: new Date().toISOString(),
      };

      existingCart.push(cartItem);
      updateLocalAndSync(existingCart, cartItem);

      console.log('✅ Cart updated with base plan:', existingCart);

      navigate('/finalize-order');
    } catch (error) {
      console.error('🔥 Error adding book to cart:', error);
      toast.error('Failed to add book to your order. Please try again.');
    }
  };

  const handleRedirectConfirm = () => {
    setShowRedirectModal(false);
    const redirectUrl = `${SHOPIFY_URL}/#pricing`;
    console.log('🔗 Redirecting to:', redirectUrl);
    window.location.href = redirectUrl;
  };

  const handleRedirectCancel = () => {
    setShowRedirectModal(false);
  };

  const getDisplayUrl = (img) => {
    if (img.croppedSketchUrl) {
      return img.croppedSketchUrl;
    }
    // Prefer the web-sized preview for display to cut image egress; fall back to
    // the full line art when no preview exists (older books / not yet generated).
    return img.sketchPreviewUrl || img.sketchUrl;
  };

  const userId = session?.user?._id;
  const role = session?.user?.role;

  // ========================================
  // SOCKET LISTENER - ONLY SOURCE OF PROGRESS DATA
  // ========================================
  useEffect(() => {
    let socket;

    if (userId && role === 'user') {
      const token =
        localStorage.getItem('auth_token') || session?.user?.accessToken;

      if (!token) {
        console.error('❌ No token found for socket authentication');
        return;
      }

      socket = io(SOCKET_URL, {
        auth: { token },
      });

      const bookMatches = (bid) =>
        bid != null &&
        activeBookId != null &&
        String(bid) === String(activeBookId);

      let hasConnected = false;
      socket.on('connect', () => {
        console.log('✅ Socket connected in Review:', socket.id);
        // Events sent while the socket was down (phone asleep, app switched)
        // are gone, so catch up from the server after a reconnect.
        if (hasConnected) void refetchBatchImagesRef.current?.();
        hasConnected = true;
      });

      // ========================================
      // COVER WORKFLOW EVENTS
      // ========================================

      socket.on('runpod:cover:started', (data) => {
        console.log('🚀 Cover started in Review:', data);
        if (bookMatches(data.bookId)) {
          setShowLineartProgress(true);
          setForceHideProgress(false);
        }
      });

      socket.on('runpod:cover:progress', (data) => {
        console.log('📊 Cover progress in Review:', data);
        if (bookMatches(data.bookId)) {
          setShowLineartProgress(true);
          setForceHideProgress(false);
        }
      });

      socket.on('runpod:cover:image_ready', (data) => {
        console.log('🖼️ Cover image ready in Review:', data);
        if (bookMatches(data.bookId)) {
          void refetchBatchImagesRef.current?.();
        }
      });

      socket.on('runpod:cover:completed', (data) => {
        console.log('✅ Cover completed in Review:', data);
        if (bookMatches(data.bookId)) {
          void refetchBatchImagesRef.current?.();
          // Cover is 5% of total; add it to the current lineart percentage
          // Use ref to avoid stale closure
          const currentPercentage = lineartProgressRef.current?.overallPercentage || 0;
          const newPercentage = Math.min(currentPercentage + 5, 100);
          console.log(`Cover done: ${currentPercentage}% + 5% = ${newPercentage}%`);
          dispatch(
            setLineartProgressByBook({
              bookId: activeBookId,
              overallPercentage: newPercentage,
            }),
          );
        }
      });

      socket.on('runpod:cover:failed', (data) => {
        console.error('❌ Cover failed in Review:', data);
      });

      socket.on('runpod:cover:error', (data) => {
        console.error('⚠️ Cover error in Review:', data);
      });

      socket.on('runpod:cover:timeout', (data) => {
        console.error('⏱️ Cover timeout in Review:', data);
      });

      // ========================================
      // LINEART WORKFLOW EVENTS
      // ========================================

      socket.on('runpod:lineart:started', (data) => {
        console.log('🚀 Lineart started in Review:', data);
        if (bookMatches(data.bookId)) {
          console.log('here process true 1')
          setShowLineartProgress(true);
          setForceHideProgress(false);
          setLineartOfferManualRetry(false);
          // Re-run: the server has already nullified the re-cropped/new rows
          // (AIUrl=null, isGenerating=true) before this event. Refetch now so
          // those rows drop their stale line art and render as loading
          // placeholders — otherwise they keep showing finished art until the
          // regenerated images arrive on batch_completed.
          void refetchBatchImagesRef.current?.();
        }
      });

      socket.on('runpod:progress', (data) => {
        if (data.workflowType === 'lineart') {
          console.log('📊 Lineart progress in Review:', data);
          if (bookMatches(data.bookId)) {
            setShowLineartProgress(true);
            setForceHideProgress(false);
          }
        }
      });

      socket.on('runpod:lineart:progress', (data) => {
        console.log('📊 Lineart progress in Review:', data);
        if (bookMatches(data.bookId)) {
          setShowLineartProgress(true);
          setForceHideProgress(false);
        }
      });

      socket.on('runpod:lineart:batch_completed', async (data) => {
        console.log('✅ Batch completed in Review:', data);

        if (bookMatches(data.bookId)) {
          console.log(
            `🔄 Refetching after batch completion: ${data.totalStored} images from batch ${data.batchNumber}`,
          );

          try {
            await refetchBatchImagesRef.current?.();

            const currentUploading = uploadingImagesRef.current || [];
            const idsToRemove =
              data.images?.map((img) => img._id || img.id).filter(Boolean) ||
              [];
            const nextUploading = currentUploading.filter(
              (id) => !idsToRemove.includes(id),
            );
            dispatch(setUploadingImages(nextUploading));

            const batchResult = await fetchBatchImagesByBookId(activeBookId);
            const batchRows = batchResult?.docs?.data || [];
            const useStagingCompletion = stagingUploadEnabledRef.current;
            const expectedCount = resolveExpectedInteriorPageCount(activeBookId, {
              activeBook: activeBookRef.current,
              lineartProgress: lineartProgressRef.current,
            });
            const finishedCount = countFinishedInteriorFromBatchRows(batchRows);
            const jobCompleteByCount = isLineartJobCompleteByCounts(
              finishedCount,
              expectedCount,
            );
            const shouldFinalize = useStagingCompletion
              ? jobCompleteByCount && !hasImagesSentToRunPending(activeBookId)
              : !hasImagesSentToRunPending(activeBookId) &&
                jobCompleteByCount &&
                (data.percentage >= 100 || data.isComplete === true);

            if (shouldFinalize) {
              const finalizeCount =
                expectedCount > 0 ? expectedCount : finishedCount;
              finalizeLineartGeneration(finalizeCount);
            }

            console.log(`✅ Batch ${data.batchNumber} refetch completed`);
          } catch (error) {
            console.error(
              '❌ Failed to refetch after batch completion:',
              error,
            );
            toast.error('Failed to load completed images');
          }
        }
      });

      socket.on('runpod:lineart:image_ready', (data) => {
        console.log('🖼️ Individual lineart image ready:', data);
        // batch_completed refetches the batch; per-image refetch re-downloads all pages.
      });

      socket.on('runpod:lineart:completed', async (data) => {
        console.log('✅ Lineart job completed in Review:', data);
        if (bookMatches(data.bookId)) {
          try {
            await refetchBatchImagesRef.current?.();

            if ((data.incompletePages ?? 0) > 0) {
              console.log(
                `Lineart worker completed with ${data.incompletePages} page(s) still needing art — not finalizing`,
              );
              return;
            }

            const batchResult = await fetchBatchImagesByBookId(activeBookId);
            const batchRows = batchResult?.docs?.data || [];
            const useStagingCompletion = stagingUploadEnabledRef.current;
            const expectedCount = resolveExpectedInteriorPageCount(activeBookId, {
              activeBook: activeBookRef.current,
              lineartProgress: lineartProgressRef.current,
            });
            const finishedCount = countFinishedInteriorFromBatchRows(batchRows);
            const jobCompleteByCount = isLineartJobCompleteByCounts(
              finishedCount,
              expectedCount,
            );
            const shouldFinalize = useStagingCompletion
              ? jobCompleteByCount && !hasImagesSentToRunPending(activeBookId)
              : !hasImagesSentToRunPending(activeBookId) && jobCompleteByCount;

            if (shouldFinalize) {
              const finalizeCount =
                expectedCount > 0
                  ? expectedCount
                  : finishedCount ||
                    data.currentLineartCount ||
                    data.totalImageCount ||
                    0;
              finalizeLineartGeneration(finalizeCount);
            }
          } catch (error) {
            console.error('Failed to verify lineart completion in Review:', error);
          }
          setLineartOfferManualRetry(false);
        }
      });

      socket.on('runpod:completed', (data) => {
        if (data?.workflowType === 'lineart' && bookMatches(data.bookId)) {
          console.log('✅ RunPod completed (lineart) in Review:', data);
          void refetchBatchImagesRef.current?.();
          setLineartOfferManualRetry(false);
        }
      });

      socket.on('runpod:lineart:failed', (data) => {
        console.error('❌ Lineart failed in Review:', data);
        if (bookMatches(data.bookId)) {
          setShowLineartProgress(false);
          setLineartOfferManualRetry(true);
          if (data?.error) {
            toast.error(data.error);
          } else {
            toast.error('Image processing failed');
          }
        }
      });

      socket.on('runpod:lineart:error', (data) => {
        console.error('⚠️ Lineart error in Review:', data);
        if (bookMatches(data.bookId)) {
          setShowLineartProgress(false);
          setLineartOfferManualRetry(true);
          toast.error(data.error || 'Processing error occurred');
        }
      });

      socket.on('runpod:lineart:timeout', (data) => {
        console.error('⏱️ Lineart timeout in Review:', data);
        if (bookMatches(data.bookId)) {
          setShowLineartProgress(false);
          setLineartOfferManualRetry(true);
          toast.error(data?.error || 'Processing took too long and was stopped.');
        }
      });

      socket.on('disconnect', () => {
        console.log('🔌 Socket disconnected from Review');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
      });
    }

    return () => {
      if (socket) {
        // Cover listeners
        socket.off('runpod:cover:started');
        socket.off('runpod:cover:progress');
        socket.off('runpod:cover:image_ready');
        socket.off('runpod:cover:completed');
        socket.off('runpod:cover:failed');
        socket.off('runpod:cover:error');
        socket.off('runpod:cover:timeout');
        // Lineart listeners
        socket.off('runpod:lineart:started');
        socket.off('runpod:lineart:progress');
        socket.off('runpod:lineart:batch_completed');
        socket.off('runpod:lineart:image_ready');
        socket.off('runpod:lineart:completed');
        socket.off('runpod:completed');
        socket.off('runpod:lineart:failed');
        socket.off('runpod:lineart:error');
        socket.off('runpod:lineart:timeout');
        // Generic
        socket.off('runpod:progress');
        socket.disconnect();
        console.log('🧹 Socket cleanup in Review');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, role, session?.token, activeBookId]);

  // Calculate initial percentage based on existing AI-processed images
  useEffect(() => {
    if (showLineartProgress && coverImages?.length && !dbSeededProgressRef.current) {
      console.log('percentage calculation happening')
      let initialPercentage = 0;

      // Check if cover has AI URL (5%)
      const hasCoverAI = coverImages?.length > 0 && !!coverImages[0]?.AIUrl;
      if (hasCoverAI) {
        initialPercentage += 5;
      }

      // Calculate lineart completion (95%) against the /run batch size, not
      // however many rows happen to be visible in Redux mid-job.
      const expectedTotal = resolveExpectedInteriorPageCount(activeBookId, {
        activeBook,
        lineartProgress,
      });
      const processedSketchImages = sketchImages.filter(
        (img) => !!img.AIUrl,
      ).length;

      if (expectedTotal > 0) {
        const lineartPercentage = (processedSketchImages / expectedTotal) * 95;
        initialPercentage += lineartPercentage;
      } else if (sketchImages?.length > 0) {
        const lineartPercentage =
          (processedSketchImages / sketchImages.length) * 95;
        initialPercentage += lineartPercentage;
      }

      const roundedPercentage = Math.round(initialPercentage);

      console.log('Initial percentage calculation:', {
        hasCoverAI,
        expectedTotal,
        totalSketchImages: sketchImages?.length || 0,
        processedSketchImages,
        coverPercentage: hasCoverAI ? 5 : 0,
        lineartPercentage: roundedPercentage - (hasCoverAI ? 5 : 0),
        totalPercentage: roundedPercentage,
      });

      // Do not claim 100% from on-screen counts while a job is still active.
      if (roundedPercentage >= 100) {
        if (isLineartJobActiveForBook(activeBookId, { activeBook, lineartProgress })) {
          return;
        }
      }

      // Only update if we have a meaningful percentage and it's different from current
      if (
        roundedPercentage > 0 &&
        roundedPercentage !== lineartProgress?.overallPercentage
      ) {
        console.log('lineart progress setting 5', roundedPercentage);
        /* setLineartProgress((cur) => ({
          ...cur,
          overallPercentage: roundedPercentage,
          totalImages: sketchImages?.length || 0,
          completedImages:
            sketchImages?.filter((img) => !!img.AIUrl).length || 0,
        })); */

        dispatch(
          setLineartProgressByBook({
            overallPercentage: roundedPercentage,
            completedImages:
              sketchImages?.filter((img) => !!img.AIUrl).length || 0,
            bookId: activeBookId,
          }),
        );

        console.log(`Set initial progress to ${roundedPercentage}%`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLineartProgress, coverImages?.length]);

  // Auto-finish when every expected interior page has line art (not just visible rows).
  useEffect(() => {
    if (!showLineartProgress || forceHideProgress || !activeBookId) return;
    // Never finish on the cached copy (it can be the previous photos' drawings)
    // or while /run hasn't answered (rows can still hold the old drawings).
    if (!batchImagesFresh || lineartProgress?.currentStage === LINEART_STAGE_STARTING) return;

    const pageItems = images.filter((img) => !img.isCover);
    if (pageItems.length === 0) return;

    const expectedCount = resolveExpectedInteriorPageCount(activeBookId, {
      activeBook,
      lineartProgress,
    });

    const jobStillActive = isLineartJobActiveForBook(activeBookId, {
      activeBook,
      lineartProgress,
    });

    if (!isInteriorGenerationComplete(pageItems, expectedCount, { jobStillActive })) {
      return;
    }

    const finishedCount =
      expectedCount > 0 ? expectedCount : pageItems.length;

    console.log(
      `Auto-finishing generation: ${finishedCount} interior page(s) ready`,
    );
    finalizeLineartGeneration(finishedCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- finalizeLineartGeneration closes over latest activeBookId
  }, [
    images,
    showLineartProgress,
    forceHideProgress,
    activeBookId,
    activeBook?.processingExpectedInteriorCount,
    lineartProgress?.totalImages,
    batchImagesFresh,
    lineartProgress?.currentStage,
  ]);

  const nonCoverStillWorking = images.some(
    (img) =>
      !img.isCover &&
      (img.isLoading || img.regenerationProcessing || !img.sketchUrl),
  );

  const showUploadProgressBar =
    showLineartProgress &&
    !forceHideProgress &&
    (!!lineartProgress?.isProcessing || nonCoverStillWorking);

  const expectedInteriorForProgress = resolveExpectedInteriorPageCount(
    activeBookId,
    { activeBook, lineartProgress },
  );
  // The card's count is the server's, the same number Page photos shows.
  const pagesStatus = lineartStatusFromRows(batchImagesData, activeBookId, {
    activeBook,
    lineartProgress,
  });
  // ---- step 5 on the redesign (docs/design/HANDOFF.md: Preview, PreviewLoading, EtsyPreview)

  const coverImage = images.find((img) => img.isCover);
  const { failed: coverFailed, retry: retryCover, retrying: coverRetrying, usedUp: coverUsedUp } = useCoverRetry(activeBookId, coverImage, {
    onRetried: () => void refetchBatchImagesRef.current?.(),
  });
  const coverSketchUrl = coverImage?.sketchUrl;
  const coverOriginalUrl = coverImage?.originalUrl;
  const seenSketchUrls = new Set();
  // Pages only: never the cover twice, never the same line art twice.
  const pageItems = images.filter((img) => {
    if (img.isCover) return false;
    if (coverSketchUrl && img.sketchUrl === coverSketchUrl) return false;
    if (coverOriginalUrl && img.originalUrl === coverOriginalUrl) return false;
    if (img.sketchUrl) {
      if (seenSketchUrls.has(img.sketchUrl)) return false;
      seenSketchUrls.add(img.sketchUrl);
    }
    return true;
  });
  const goToCoverPhoto = () => {
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: 'yes', stepCode: 'Upload_Your_Cover_Photo' });
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Your_Cover_Photo' }));
    navigate(`/dashboard?stepCode=Upload_Your_Cover_Photo${tag ? `&tag=${tag}` : ''}`);
  };
  /** "Replace photo" on a failed page: Page photos with that page's crop screen open (its Replace photo does the rest). */
  const replacePagePhoto = (img) => {
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: 'yes', stepCode: 'Upload_Images' });
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Images' }));
    navigate(`/create?stepCode=Upload_Images&crop=${encodeURIComponent(String(img.id))}${tag ? `&tag=${tag}` : ''}`);
  };
  const retryPages = async (ids) => {
    if (!ids.length || retryingPageIds.length) return;
    setRetryingPageIds(ids);
    const result = await retryDrawing({ bookId: activeBookId, mediaIds: ids });
    setRetryingPageIds([]);
    const patch = (fields) => setImages((prev) => prev.map((img) => (ids.includes(String(img.id)) ? { ...img, ...fields(img) } : img)));
    if (result.ok) {
      patch((img) => ({ drawStatus: 'drawing', isLoading: true, drawRetries: (img.drawRetries || 0) + 1 }));
      void refetchBatchImagesRef.current?.();
    } else if (result.code === 'RETRY_LIMIT') {
      patch(() => ({ drawRetries: MAX_DRAW_RETRIES }));
    } else {
      toast.error(result.message || "We couldn't start that. Please try again.");
    }
  };

  // Pages that failed to draw (API drawStatus; PreviewFailed): "Try again" re-sends the stored
  // photo, 2 tries each; "Replace photo" opens that page's crop screen on Page photos.
  const [dedicationUpClose, setDedicationUpClose] = useState(false);
  const isFailedPage = (img) => img.drawStatus === 'failed' && !img.sketchUrl;
  const failedPages = pageItems.map((img, i) => ({ img, number: i + 1 })).filter(({ img }) => isFailedPage(img));
  const retryablePages = failedPages.filter(({ img }) => !drawRetriesUsedUp(img));
  const viewerPages = [
    {
      id: coverImage?.id || 'cover',
      label: 'Cover',
      isCover: true,
      url: coverImage ? getDisplayUrl(coverImage) : null,
      ready: coverFailed || (!!coverImage?.sketchUrl && !coverImage?.isLoading),
      // The cover as it prints: drawing, brush band and title.
      content: coverImage ? <CoverPreview imageUrl={getDisplayUrl(coverImage)} title={activeBook?.title} className="h-full" /> : null,
      ...(coverFailed
        ? {
            failed: true,
            content: (
              <CoverDrawFailedBox
                onRetry={retryCover}
                onChangePhoto={goToCoverPhoto}
                retrying={coverRetrying}
                usedUp={coverUsedUp}
                className="rounded-lg"
              />
            ),
          }
        : {}),
    },
    // The inside front cover, as it prints: only when the dedication has a message (same rule as print).
    // TODO(design): the Preview mockups don't draw this page (docs/design/GAPS.md); it copies the printed page.
    // Without a dedication that page prints blank (decided 2026-10-23); it still faces page 1 and is labeled
    // "Dedication page" rather than "Blank" (decided 2026-10-23), so it reads as the place for one.
    forewordPrints(activeBook?.description)
      ? {
          id: 'dedication',
          label: 'Dedication',
          ready: true,
          content: <ForewordPage introKeyword={activeBook?.introKeyword} description={activeBook?.description} className="h-full w-full" />,
          // "Read up close" (decided 2026-10-26): in the page's bottom margin, or below the page when the message reaches it.
          action: {
            label: 'Read up close',
            ariaLabel: 'Read your dedication up close',
            icon: <Search className="h-3.5 w-3.5 md:h-4 md:w-4" strokeWidth={2.4} aria-hidden="true" />,
            onClick: () => setDedicationUpClose(true),
            below: forewordReachesBottom({ introKeyword: activeBook?.introKeyword, description: activeBook?.description }),
          },
        }
      : {
          id: 'blank-inside',
          label: 'Dedication page',
          ready: true,
          blank: true,
          dedication: true,
          // Tapping it goes to step 4 with the dedication editor open (decided 2026-10-23).
          onOpen: () => goBackToCoverStep({ openNote: true }),
          openLabel: 'Add a dedication',
        },
    ...pageItems.map((img, i) =>
      isFailedPage(img)
        ? {
            id: img.id,
            label: `Page ${i + 1}`,
            ready: true,
            failed: true,
            content: (
              <PageDrawFailed
                pageNumber={i + 1}
                onRetry={() => retryPages([String(img.id)])}
                onReplace={() => replacePagePhoto(img)}
                retrying={retryingPageIds.includes(String(img.id))}
                usedUp={drawRetriesUsedUp(img)}
              />
            ),
          }
        : {
            id: img.id,
            label: `Page ${i + 1}`,
            url: getDisplayUrl(img),
            ready: !!img.sketchUrl && !img.isLoading && !img.regenerationProcessing,
          }
    ),
  ];
  // The last spread holds two pages: a lone last page faces a blank one (photo 20 faces the inside back cover).
  // With all 20 photos that page is the inside back cover; with fewer it's a blank interior page.
  if (viewerPages.length % 2 === 0) {
    viewerPages.push({ id: 'blank-end', label: pageItems.length >= 20 ? 'Inside back cover' : 'Blank', ready: true, blank: true });
  }
  // Open on the first page that failed, once, when failures show up.
  const firstFailedIndex = viewerPages.findIndex((page) => page.failed);
  const jumpedToFailureRef = useRef(false);
  useEffect(() => {
    if (firstFailedIndex >= 0 && !jumpedToFailureRef.current) {
      jumpedToFailureRef.current = true;
      setViewerIndex(firstFailedIndex);
    }
  }, [firstFailedIndex]);
  const expectedPages = pagesStatus.total || expectedInteriorForProgress || pageItems.length;
  const drawingPages = showUploadProgressBar;
  // The cover draws on its own job, so it can still be drawing after every page is done (or before
  // any page starts). The status card covers it too, instead of disappearing while the cover tile waits.
  const drawingCover = !!coverImage && !coverFailed && (!coverImage.sketchUrl || !!coverImage.isLoading);
  const drawingTitle = drawingPages && drawingCover ? 'Drawing your cover and pages' : drawingPages ? 'Drawing your pages' : 'Drawing your cover';

  // `openNote`: arrive with the dedication editor open (`note=1`, read by CoverEditor); 'message' puts
  // the cursor in its Message field instead of the Heading (Edit dedication from the up-close view).
  const goBackToCoverStep = ({ openNote = false } = {}) => {
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: 'yes', stepCode: 'Design_Your_Cover_Page' });
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Design_Your_Cover_Page' }));
    navigate(`/cover-image?stepCode=${'Design_Your_Cover_Page'}${tag ? `&tag=${tag}` : ''}${openNote === 'message' ? '&note=message' : openNote ? '&note=1' : ''}`);
  };
  headerBackRef.current = goBackToCoverStep;


  const primaryLabel = giftId
    ? "I'm done with this book"
    : isEtsy
      ? 'Finalize your order'
      : 'Looks good, review order';
  const primaryAction = giftId ? handleGiftDone : isEtsy ? handleEtsySubmit : handleAddToCart;
  const primary = (
    <Button size="lg" fullWidth onClick={() => primaryAction(true)} busy={isCheckoutLoading} disabled={isLoadingBatchImages}>
      {isCheckoutLoading ? (isEtsy ? 'Sending to print' : 'One moment') : primaryLabel}
    </Button>
  );
  // TODO(design): the Etsy line assumes a printed book; digital-only orders need their own wording (docs/design/etsy-gift-review.md).
  const primaryNote = isEtsy ? 'Once you finalize, we send your book to print.' : giftId ? 'You can still make changes until you send your books to print.' : null;

  const sidePanel = (
    <BookSidePanel
      cover={<CoverPreview imageUrl={coverImage && !coverFailed ? getDisplayUrl(coverImage) : null} title={activeBook?.title} />}
      title={activeBook?.title}
      rows={[
        { label: `Cover${forewordPrints(activeBook?.description) ? ', dedication' : ''} + ${pageItems.length} ${pageItems.length === 1 ? 'page' : 'pages'}`, value: '' },
        isEtsy
          ? { label: 'Order', value: 'Paid on Etsy' }
          : giftId
            ? { label: 'Order', value: 'A gift' }
            : isPdfOnly
              ? { label: 'Printable PDF', value: `$${PDF_DOWNLOAD_PRICE.toFixed(2)}` }
              : { label: 'Price', value: `$${BASE_BOOK_PRICE.toFixed(2)}` },
      ]}
      primary={primary}
      note={primaryNote}
      back={{ onClick: goBackToCoverStep }}
      compact
    />
  );
  // The gift note isn't shown on phones (decided 2026-10-23); tablet and desktop keep it.
  const bottomBar = <BottomBar primary={primary} back={{ onClick: goBackToCoverStep }} note={primaryNote} noteClassName={giftId ? 'hidden md:block' : undefined} />;

  return (
    <>
      <WizardShell aside={sidePanel} bottomBar={bottomBar}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            {/* PreviewPdf: the "PDF only" tag sits beside the heading from tablet up (in the step row on phones). */}
            <div className="flex items-center gap-3">
              <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Flip through your book</h1>
              {isPdfOnly ? <StatusLabel status="pdf" className="hidden md:inline-flex" /> : null}
            </div>
            <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">
              <span className="lg:hidden">Swipe to see every page side by side. </span>
              <span className="hidden lg:inline">See every page side by side. </span>
              {isPdfOnly ? "The watermark won't appear in your PDF." : "The watermark won't appear in your printed book."}
            </p>
          </div>

          {drawingPages || drawingCover ? (
            // TODO(design): the cover-only and cover-and-pages versions of this card have no mockup (docs/design/GAPS.md).
            <div role="status" className="flex flex-col gap-2 rounded-xl border border-md-pink-light-1 bg-white px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-md-pink-dark" aria-hidden="true" />
                <span className="flex-1 text-sm font-bold text-md-dark">{drawingTitle}</span>
                {drawingPages ? (
                  <span className="text-sm font-bold text-md-dark">
                    {pagesStatus.done}
                    <span className="font-medium text-md-muted"> of {expectedPages} ready</span>
                  </span>
                ) : null}
              </div>
              <div className="h-1 overflow-hidden rounded-sm bg-md-pink-light-3" aria-hidden="true">
                {drawingPages ? (
                  <div className="h-full bg-md-pink transition-[width] duration-500" style={{ width: `${expectedPages ? Math.round((pagesStatus.done / expectedPages) * 100) : 0}%` }} />
                ) : (
                  // No page count for the cover alone: a moving bar says it's still working.
                  <div className="h-full w-1/3 animate-pulse rounded-sm bg-md-pink" />
                )}
              </div>
              <span className="text-[13px] leading-snug text-md-muted">
                {drawingPages ? (
                  <>
                    <span className="hidden lg:inline">Pages show up as they're finished. </span>
                    You don't have to wait. Keep going and we'll finish them.
                  </>
                ) : (
                  "Your cover shows up here when it's ready. You don't have to wait. Keep going and we'll finish it."
                )}
              </span>
            </div>
          ) : null}

          {failedPages.length ? (
            <div role="alert" className="flex flex-col gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm leading-snug text-red-900">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="mt-px h-[18px] w-[18px] flex-shrink-0 text-red-700" strokeWidth={2.2} aria-hidden="true" />
                <span>
                  {failedPages.length === 1 ? (
                    <>
                      <strong>We couldn't draw 1 page.</strong> Page {failedPages[0].number} is shown below. Try again or replace the photo. Your other pages are ready.
                    </>
                  ) : (
                    <>
                      <strong>We couldn't draw {failedPages.length} pages:</strong> {pagesList(failedPages.map((p) => p.number))}. Try again or pick different photos. Your other pages are ready.
                    </>
                  )}
                </span>
              </div>
              {failedPages.length > 1 && retryablePages.length ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start !border !bg-white"
                  onClick={() => retryPages(retryablePages.map(({ img }) => String(img.id)))}
                  busy={retryingPageIds.length > 1}
                  icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}
                >
                  Try them all again
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* The older note stays for failures the API couldn't pin to a page. */}
          {lineartOfferManualRetry && !showLineartProgress && !failedPages.length ? (
            // TODO(design): pages that failed to draw have no mockup (docs/design/GAPS.md).
            <div role="status" className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3.5 md:flex-row md:items-center md:justify-between">
              <p className="m-0 text-sm text-amber-950">Some pages didn't finish drawing. Send the same photos again; you don't need to re-upload them.</p>
              <Button variant="secondary" size="sm" onClick={handleRetryLineartAfterFailure} busy={lineartManualRetryBusy} icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}>
                {lineartManualRetryBusy ? 'Starting' : 'Try line art again'}
              </Button>
            </div>
          ) : null}

          {isLoadingBatchImages ? (
            <SkeletonGroup label="Loading your book" className="flex flex-col items-center gap-4">
              <Skeleton shape="block" className="h-[360px] w-[270px] rounded-lg" />
              <div className="flex gap-2">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} shape="block" className="h-[58px] w-11 rounded-md" />
                ))}
              </div>
            </SkeletonGroup>
          ) : (
            <>
              <PageViewer pages={viewerPages} initialIndex={viewerIndex} onIndexChange={setViewerIndex} />
              <DedicationUpClose
                open={dedicationUpClose}
                onClose={() => setDedicationUpClose(false)}
                onEdit={() => goBackToCoverStep({ openNote: 'message' })}
                introKeyword={activeBook?.introKeyword}
                description={activeBook?.description}
              />
            </>
          )}
        </div>
      </WizardShell>

      <RedirectModal isOpen={showRedirectModal} onClose={handleRedirectCancel} onConfirm={handleRedirectConfirm} />
    </>
  );
};

export default ReviewBookContent;